import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Empty,
  Input,
  List,
  Menu,
  Modal,
  Popconfirm,
  Radio,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message
} from 'antd';
import { CheckOutlined, DownloadOutlined, PlusOutlined, RobotOutlined, UnlockOutlined } from '@ant-design/icons';
import {
  applyNeedsAnalysisAiSuggestions,
  approveNeedsAnalysis,
  createNeedsAnalysisCustomItem,
  deleteNeedsAnalysisCustomItem,
  downloadNeedsAnalysisPdf,
  fetchNeedsAnalysis,
  reopenNeedsAnalysis,
  updateNeedsAnalysis
} from './api';
import type {
  NeedsAnalysisAnswer,
  NeedsAnalysisItem,
  NeedsAnalysisResponse,
  NeedsAnalysisSection,
  NeedsAnalysisUpdatePayload
} from './needsAnalysisTypes';

const { Paragraph, Text, Title } = Typography;

type Props = {
  leadId: string;
  canManage: boolean;
};

type EditableRow = {
  key: string;
  itemId?: number;
  customItemId?: string;
  label: string;
  answerType: string;
  options: Record<string, unknown> | null;
  indent: number;
  answer: NeedsAnalysisAnswer;
};

const MIN_LOCATION_ROWS = 5;

type LocationEntry = { address: string; note: string };

function parseLocationEntries(value: unknown): LocationEntry[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.map((entry) => {
    if (typeof entry === 'string') {
      const [address, ...rest] = entry.split(' · ');
      return { address: address.trim(), note: rest.join(' · ').trim() };
    }
    const row = (entry || {}) as Record<string, unknown>;
    return {
      address: typeof row.address === 'string' ? row.address : '',
      note: typeof row.note === 'string' ? row.note : ''
    };
  }).filter((entry) => entry.address || entry.note);
}

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function sourceTag(answer: NeedsAnalysisAnswer) {
  if (answer.source === 'AI_SUGGESTION') {
    return <Tag color="blue">AI-forslag</Tag>;
  }
  if (answer.source === 'BROKER' && answer.ai_suggested_value) {
    return <Tag color="orange">Rettet af mægler</Tag>;
  }
  if (answer.source === 'BROKER') {
    return <Tag color="green">Mægler</Tag>;
  }
  return <Tag>Standard</Tag>;
}

function itemDepth(items: NeedsAnalysisItem[]): Map<number, number> {
  const depth = new Map<number, number>();
  for (const item of items) {
    depth.set(item.id, item.parent_item_id ? (depth.get(item.parent_item_id) ?? 0) + 1 : 0);
  }
  return depth;
}

function buildRows(section: NeedsAnalysisSection): EditableRow[] {
  const depth = itemDepth(section.items);
  const rows: EditableRow[] = section.items.map((item) => ({
    key: `item-${item.id}`,
    itemId: item.id,
    label: item.label,
    answerType: item.answer_type,
    options: item.options,
    indent: depth.get(item.id) ?? 0,
    answer: item.answer
  }));

  for (const custom of section.custom_items) {
    rows.push({
      key: `custom-${custom.id}`,
      customItemId: custom.id,
      label: custom.label,
      answerType: custom.answer_type,
      options: null,
      indent: 0,
      answer: custom.answer
    });
  }

  return rows;
}

function collectSections(section: NeedsAnalysisSection): NeedsAnalysisSection[] {
  return [section, ...(section.groups || [])];
}

export default function NeedsAnalysisPanel({ leadId, canManage }: Props) {
  const [data, setData] = useState<NeedsAnalysisResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [activeSectionCode, setActiveSectionCode] = useState<string | null>(null);
  const [customItemModal, setCustomItemModal] = useState<{ sectionId: number } | null>(null);
  const [customItemLabel, setCustomItemLabel] = useState('');
  const [customItemType, setCustomItemType] = useState('COVERED');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchNeedsAnalysis(leadId);
      setData(response);
      setActiveSectionCode((current) => current || response.sections[0]?.code || null);
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke hente behovsanalysen');
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    void load();
  }, [load]);

  const isApproved = data?.analysis.status === 'APPROVED';
  const editable = canManage && !isApproved;

  const activeSection = useMemo(
    () => data?.sections.find((section) => section.code === activeSectionCode) || null,
    [data, activeSectionCode]
  );

  const patchSection = useCallback((sectionId: number, patch: Partial<NeedsAnalysisSection['answer']>) => {
    setData((current) => {
      if (!current) return current;
      const next = clone(current);
      for (const top of next.sections) {
        for (const section of collectSections(top)) {
          if (section.id !== sectionId) continue;
          section.answer = { ...section.answer, ...patch };

          // Turning a section off means nothing in it is covered.
          if (patch.wanted === false) {
            const scope = section.id === top.id ? collectSections(top) : [section];
            for (const target of scope) {
              for (const item of target.items) {
                if (item.answer.choice === 'NEJ') continue;
                item.answer = { ...item.answer, choice: 'NEJ', source: 'BROKER' };
              }
              for (const custom of target.custom_items) {
                if (custom.answer.choice === 'NEJ') continue;
                custom.answer = { ...custom.answer, choice: 'NEJ', source: 'BROKER' };
              }
            }
          }
        }
      }
      return next;
    });
  }, []);

  const patchAnswer = useCallback((rowKey: string, patch: Partial<NeedsAnalysisAnswer>) => {
    setData((current) => {
      if (!current) return current;
      const next = clone(current);
      for (const top of next.sections) {
        for (const section of collectSections(top)) {
          for (const item of section.items) {
            if (`item-${item.id}` === rowKey) {
              item.answer = { ...item.answer, ...patch, source: 'BROKER' };
            }
          }
          for (const custom of section.custom_items) {
            if (`custom-${custom.id}` === rowKey) {
              custom.answer = { ...custom.answer, ...patch, source: 'BROKER' };
            }
          }
        }
      }
      return next;
    });
  }, []);

  const saveSection = useCallback(async () => {
    if (!activeSection) return;
    setSaving(true);
    try {
      const payload: NeedsAnalysisUpdatePayload = { section_answers: [], item_answers: [] };
      for (const section of collectSections(activeSection)) {
        payload.section_answers!.push({
          section_id: section.id,
          wanted: section.answer.wanted,
          risk_description: section.answer.risk_description,
          note: section.answer.note
        });
        for (const item of section.items) {
          payload.item_answers!.push({
            item_id: item.id,
            choice: item.answer.choice,
            value_text: item.answer.value_text,
            value_json: item.answer.value_json,
            note: item.answer.note
          });
        }
        for (const custom of section.custom_items) {
          payload.item_answers!.push({
            custom_item_id: custom.id,
            choice: custom.answer.choice,
            value_text: custom.answer.value_text,
            value_json: custom.answer.value_json,
            note: custom.answer.note
          });
        }
      }
      const response = await updateNeedsAnalysis(leadId, payload);
      setData(response);
      message.success('Afsnittet er gemt');
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke gemme behovsanalysen');
    } finally {
      setSaving(false);
    }
  }, [activeSection, leadId]);

  const runAction = useCallback(async (action: () => Promise<void>) => {
    setBusy(true);
    try {
      await action();
    } catch (error: any) {
      message.error(error?.message || 'Handlingen kunne ikke gennemføres');
    } finally {
      setBusy(false);
    }
  }, []);

  const downloadPdf = useCallback(() => runAction(async () => {
    const blob = await downloadNeedsAnalysisPdf(leadId);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `behovsanalyse-v${data?.analysis.version_number ?? 1}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }), [data?.analysis.version_number, leadId, runAction]);

  const renderAnswerControl = (row: EditableRow) => {
    const disabled = !editable;
    switch (row.answerType) {
      case 'COVERED':
      case 'YES_NO':
        return (
          <Radio.Group
            disabled={disabled}
            value={row.answer.choice}
            onChange={(event) => patchAnswer(row.key, { choice: event.target.value })}
            optionType="button"
            size="small"
            options={[
              { label: 'Ja', value: 'JA' },
              { label: 'Nej', value: 'NEJ' },
              { label: 'Fravalgt af kunde', value: 'FRAVALGT' }
            ]}
          />
        );
      case 'GEOGRAPHY': {
        const values = Array.isArray(row.options?.values) ? (row.options!.values as string[]) : [];
        return (
          <Select
            disabled={disabled}
            allowClear
            size="small"
            style={{ minWidth: 200 }}
            value={row.answer.value_text || undefined}
            onChange={(value) => patchAnswer(row.key, { value_text: value ?? null })}
            options={values.map((value) => ({ value, label: value }))}
          />
        );
      }
      case 'AMOUNT':
        return (
          <Input
            disabled={disabled}
            size="small"
            style={{ minWidth: 140 }}
            value={row.answer.value_text || ''}
            placeholder="Beløb / antal"
            onChange={(event) => patchAnswer(row.key, { value_text: event.target.value })}
          />
        );
      case 'LOCATION_SET':
      case 'MATRIX':
        return <Text type="secondary">Udfyldes nedenfor</Text>;
      default:
        return (
          <Input
            disabled={disabled}
            size="small"
            value={row.answer.value_text || ''}
            onChange={(event) => patchAnswer(row.key, { value_text: event.target.value })}
          />
        );
    }
  };

  const renderLocationSets = (section: NeedsAnalysisSection) => {
    const locationItems = section.items.filter((item) => item.answer_type === 'LOCATION_SET');
    return locationItems.map((item) => {
      const entries = parseLocationEntries(item.answer.value_json);
      const rowCount = Math.max(entries.length + 1, MIN_LOCATION_ROWS);

      const updateEntry = (index: number, patch: Partial<LocationEntry>) => {
        const next = Array.from({ length: rowCount }, (_, position) => ({
          address: entries[position]?.address || '',
          note: entries[position]?.note || ''
        }));
        next[index] = { ...next[index], ...patch };
        patchAnswer(`item-${item.id}`, {
          value_json: next.filter((entry) => entry.address.trim() || entry.note.trim())
        });
      };

      return (
        <Card key={item.id} size="small" title={item.label} style={{ marginTop: 12 }}>
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            {Array.from({ length: rowCount }, (_, index) => (
              <Space key={index} size={8} style={{ width: '100%' }} align="start">
                <Text type="secondary" style={{ width: 130, display: 'inline-block', paddingTop: 5 }}>
                  Forsikringssted {index + 1}
                </Text>
                <Input
                  disabled={!editable}
                  style={{ width: 420 }}
                  placeholder="Adresse"
                  value={entries[index]?.address || ''}
                  onChange={(event) => updateEntry(index, { address: event.target.value })}
                />
                <Input
                  disabled={!editable}
                  style={{ width: 320 }}
                  placeholder="Bemærkning"
                  value={entries[index]?.note || ''}
                  onChange={(event) => updateEntry(index, { note: event.target.value })}
                />
              </Space>
            ))}
          </Space>
        </Card>
      );
    });
  };

  const renderMatrix = (section: NeedsAnalysisSection) => {
    const matrixItems = section.items.filter((item) => item.answer_type === 'MATRIX');
    return matrixItems.map((item) => {
      const columns = Array.isArray(item.options?.columns) ? (item.options!.columns as string[]) : [];
      const matrixRows = Array.isArray(item.options?.rows) ? (item.options!.rows as string[]) : [];
      const values = (item.answer.value_json && typeof item.answer.value_json === 'object'
        ? item.answer.value_json
        : {}) as Record<string, Record<string, string>>;

      return (
        <Card key={item.id} size="small" title={item.label} style={{ marginTop: 12 }}>
          <Table
            rowKey={(row) => row as unknown as string}
            size="small"
            pagination={false}
            dataSource={matrixRows}
            columns={[
              { title: 'Post', render: (_: unknown, row: string) => row },
              ...columns.map((column) => ({
                title: column,
                render: (_: unknown, row: string) => (
                  <Input
                    disabled={!editable}
                    size="small"
                    value={values?.[row]?.[column] || ''}
                    onChange={(event) => {
                      const next = clone(values || {});
                      next[row] = { ...(next[row] || {}), [column]: event.target.value };
                      patchAnswer(`item-${item.id}`, { value_json: next });
                    }}
                  />
                )
              }))
            ]}
          />
        </Card>
      );
    });
  };

  const renderSectionBlock = (section: NeedsAnalysisSection, isGroup: boolean) => {
    const rows = buildRows(section).filter(
      (row) => row.answerType !== 'MATRIX' && row.answerType !== 'LOCATION_SET'
    );
    return (
      <Card
        key={section.id}
        size={isGroup ? 'small' : 'default'}
        title={isGroup ? `${section.code} ${section.title}` : undefined}
        style={{ marginTop: isGroup ? 12 : 0 }}
        extra={editable ? (
          <Button
            size="small"
            icon={<PlusOutlined />}
            onClick={() => {
              setCustomItemModal({ sectionId: section.id });
              setCustomItemLabel('');
              setCustomItemType('COVERED');
            }}
          >
            Tilføj punkt
          </Button>
        ) : undefined}
      >
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
          {section.has_wanted_question ? (
            <Space size={10}>
              <Text strong>Ønskes denne forsikring</Text>
              <Switch
                disabled={!editable}
                checked={section.answer.wanted}
                checkedChildren="Ja"
                unCheckedChildren="Nej"
                onChange={(checked) => patchSection(section.id, { wanted: checked })}
              />
            </Space>
          ) : null}

          {section.has_risk_description ? (
            <Input.TextArea
              disabled={!editable}
              autoSize={{ minRows: 2, maxRows: 6 }}
              placeholder="Risikobeskrivelse"
              value={section.answer.risk_description || ''}
              onChange={(event) => patchSection(section.id, { risk_description: event.target.value })}
            />
          ) : null}

          {section.intro_text ? <Paragraph type="secondary">{section.intro_text}</Paragraph> : null}

          {rows.length ? (
            <Table<EditableRow>
              rowKey="key"
              size="small"
              pagination={false}
              dataSource={rows}
              columns={[
                {
                  title: 'Dækning',
                  dataIndex: 'label',
                  width: '32%',
                  render: (_, row) => (
                    <span style={{ paddingLeft: row.indent * 16 }}>
                      {row.label}
                      {row.customItemId ? <Tag style={{ marginLeft: 8 }}>Tilføjet</Tag> : null}
                    </span>
                  )
                },
                { title: 'Dækkes', width: '24%', render: (_, row) => renderAnswerControl(row) },
                {
                  title: 'Sum / selvrisiko',
                  width: '18%',
                  render: (_, row) => (row.answerType === 'COVERED' || row.answerType === 'YES_NO' ? (
                    <Input
                      disabled={!editable}
                      size="small"
                      placeholder="Sum, selvrisiko"
                      value={row.answer.value_text || ''}
                      onChange={(event) => patchAnswer(row.key, { value_text: event.target.value })}
                    />
                  ) : null)
                },
                {
                  title: 'Bemærkning',
                  width: '18%',
                  render: (_, row) => (
                    <Input
                      disabled={!editable}
                      size="small"
                      value={row.answer.note || ''}
                      onChange={(event) => patchAnswer(row.key, { note: event.target.value })}
                    />
                  )
                },
                {
                  title: 'Kilde',
                  width: '10%',
                  render: (_, row) => (
                    <Space size={4} direction="vertical">
                      {sourceTag(row.answer)}
                      {row.customItemId && editable ? (
                        <Popconfirm
                          title="Slet punktet?"
                          okText="Slet"
                          cancelText="Annuller"
                          onConfirm={() => runAction(async () => {
                            await deleteNeedsAnalysisCustomItem(leadId, row.customItemId!);
                            await load();
                          })}
                        >
                          <Button size="small" type="link" danger>Slet</Button>
                        </Popconfirm>
                      ) : null}
                    </Space>
                  )
                }
              ]}
            />
          ) : null}

          {renderLocationSets(section)}
          {renderMatrix(section)}

          {section.has_note ? (
            <Input.TextArea
              disabled={!editable}
              autoSize={{ minRows: 2, maxRows: 4 }}
              placeholder="Bemærkninger til afsnittet"
              value={section.answer.note || ''}
              onChange={(event) => patchSection(section.id, { note: event.target.value })}
            />
          ) : null}
        </Space>
      </Card>
    );
  };

  return (
    <Card
      title="Behovsanalyse"
      loading={loading}
      extra={data ? (
        <Space size={8} wrap>
          <Tag>Version {data.analysis.version_number}</Tag>
          <Tag color={isApproved ? 'success' : 'processing'}>{isApproved ? 'Godkendt' : 'Kladde'}</Tag>
        </Space>
      ) : undefined}
    >
      {!data ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Behovsanalysen er ikke tilgængelig" />
      ) : (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          {isApproved ? (
            <Alert
              type="success"
              showIcon
              message="Behovsanalysen er godkendt og låst"
              description="Genåbn analysen for at rette i den. Den godkendte version bevares som dokumentation."
            />
          ) : (
            <Alert
              type="info"
              showIcon
              message="Alle punkter besvares. Punkter uden svar står som Nej og medtages i PDF'en som dokumentation for rådgivningen. Brug Fravalgt af kunde, når kunden aktivt har sagt nej til en dækning."
            />
          )}

          <Space size={8} wrap>
            {editable ? (
              <Button
                icon={<RobotOutlined />}
                loading={busy}
                onClick={() => runAction(async () => {
                  const result = await applyNeedsAnalysisAiSuggestions(leadId);
                  setData(result.payload);
                  message.success(
                    `${result.updated_sections} afsnit og ${result.updated_items} punkter er udfyldt fra AI-analysen`
                  );
                })}
              >
                Hent forslag fra AI-analyse
              </Button>
            ) : null}
            {editable ? (
              <Button
                type="primary"
                icon={<CheckOutlined />}
                loading={busy}
                onClick={() => runAction(async () => {
                  setData(await approveNeedsAnalysis(leadId));
                  message.success('Behovsanalysen er godkendt');
                })}
              >
                Godkend behovsanalyse
              </Button>
            ) : null}
            {canManage && isApproved ? (
              <Button
                icon={<UnlockOutlined />}
                loading={busy}
                onClick={() => runAction(async () => {
                  setData(await reopenNeedsAnalysis(leadId));
                  message.success('Behovsanalysen er genåbnet som ny version');
                })}
              >
                Genåbn til redigering
              </Button>
            ) : null}
            <Button icon={<DownloadOutlined />} loading={busy} onClick={downloadPdf}>
              Hent PDF
            </Button>
          </Space>

          <Card size="small" title="Virksomhedsoplysninger">
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              {data.company_info_fields.map((field) => (
                <Space key={field.key} size={8} style={{ width: '100%' }}>
                  <Text style={{ width: 220, display: 'inline-block' }}>{field.label}</Text>
                  <Input
                    disabled={!editable}
                    size="small"
                    style={{ minWidth: 320 }}
                    value={data.analysis.company_info[field.key] || ''}
                    onChange={(event) => setData((current) => {
                      if (!current) return current;
                      const next = clone(current);
                      next.analysis.company_info[field.key] = event.target.value;
                      return next;
                    })}
                  />
                </Space>
              ))}
              <Input.TextArea
                disabled={!editable}
                autoSize={{ minRows: 2, maxRows: 5 }}
                placeholder="Virksomhedens aktiviteter"
                value={data.analysis.activities_text || ''}
                onChange={(event) => setData((current) => {
                  if (!current) return current;
                  const next = clone(current);
                  next.analysis.activities_text = event.target.value;
                  return next;
                })}
              />
              {editable ? (
                <Button
                  size="small"
                  loading={saving}
                  onClick={() => runAction(async () => {
                    setData(await updateNeedsAnalysis(leadId, {
                      company_info: data.analysis.company_info,
                      activities_text: data.analysis.activities_text
                    }));
                    message.success('Virksomhedsoplysninger gemt');
                  })}
                >
                  Gem virksomhedsoplysninger
                </Button>
              ) : null}
            </Space>
          </Card>

          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
            <Menu
              mode="inline"
              style={{ width: 280, flex: '0 0 280px' }}
              selectedKeys={activeSectionCode ? [activeSectionCode] : []}
              onSelect={({ key }) => setActiveSectionCode(String(key))}
              items={data.sections.map((section) => ({
                key: section.code,
                label: (
                  <Space size={6}>
                    <span>{section.code}. {section.title}</span>
                    {section.answer.wanted ? <Tag color="green">Ja</Tag> : null}
                  </Space>
                )              }))}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              {activeSection ? (
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
                    <Title level={4} style={{ margin: 0 }}>
                      {activeSection.code}. {activeSection.title}
                    </Title>
                    {editable ? (
                      <Button type="primary" loading={saving} onClick={saveSection}>
                        Gem afsnit
                      </Button>
                    ) : null}
                  </Space>
                  {renderSectionBlock(activeSection, false)}
                  {(activeSection.groups || []).map((group) => renderSectionBlock(group, true))}
                </Space>
              ) : null}
            </div>
          </div>

          {data.approvals.length ? (
            <Card size="small" title="Tidligere godkendelser">
              <List
                size="small"
                dataSource={data.approvals}
                renderItem={(approval) => (
                  <List.Item key={approval.id}>
                    <Space size={8} wrap>
                      <Tag>Version {approval.version_number}</Tag>
                      <Text>{new Date(approval.approved_at).toLocaleString('da-DK')}</Text>
                      {approval.note ? <Text type="secondary">{approval.note}</Text> : null}
                    </Space>
                  </List.Item>
                )}
              />
            </Card>
          ) : null}
        </Space>
      )}

      <Modal
        open={Boolean(customItemModal)}
        title="Tilføj punkt"
        okText="Tilføj"
        cancelText="Annuller"
        confirmLoading={busy}
        onCancel={() => setCustomItemModal(null)}
        onOk={() => runAction(async () => {
          if (!customItemModal || !customItemLabel.trim()) {
            message.error('Angiv en tekst for punktet');
            return;
          }
          await createNeedsAnalysisCustomItem(leadId, {
            section_id: customItemModal.sectionId,
            label: customItemLabel.trim(),
            answer_type: customItemType
          });
          setCustomItemModal(null);
          await load();
          message.success('Punktet er tilføjet');
        })}
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Input
            autoFocus
            placeholder="Tekst for punktet"
            value={customItemLabel}
            onChange={(event) => setCustomItemLabel(event.target.value)}
          />
          <Select
            style={{ width: '100%' }}
            value={customItemType}
            onChange={setCustomItemType}
            options={[
              { value: 'COVERED', label: 'Forsikres / Forsikres ikke' },
              { value: 'YES_NO', label: 'Ja / Nej' },
              { value: 'TEXT', label: 'Fritekst' },
              { value: 'AMOUNT', label: 'Beløb / antal' }
            ]}
          />
        </Space>
      </Modal>
    </Card>
  );
}
